<script setup lang="ts">
defineOptions({
  inheritAttrs: false,
});
import type {
  TooltipArrowProps,
  TooltipContentProps,
  TooltipRootEmits,
  TooltipRootProps,
  TooltipTriggerProps,
} from 'reka-ui';
import { TooltipArrow, TooltipContent, TooltipPortal, TooltipRoot, TooltipTrigger } from 'reka-ui';
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';

import { cnMerge } from '@/utils';

const portalTo = ref<string | undefined>(undefined);
onMounted(() => {
  portalTo.value = document.getElementById('main-content') ? '#main-content' : undefined;
});

type GenericTooltipProps = {
  class?: string;
};

export type KaTooltipTriggerProps = TooltipTriggerProps & GenericTooltipProps;
export type KaTooltipContentProps = TooltipContentProps & GenericTooltipProps;
export type KaTooltipArrowProps = TooltipArrowProps & GenericTooltipProps;

export type KaTooltipProps = {
  content?: string;
  contentContainerClass?: string;
  triggerProps?: KaTooltipTriggerProps;
  contentProps?: KaTooltipContentProps;
  rootProps?: TooltipRootProps;
  arrowProps?: KaTooltipArrowProps;
  dataTestid?: string;
  ariaLabel?: string;
  clickToToggle?: boolean;
};

const props = withDefaults(defineProps<KaTooltipProps>(), {
  arrowProps: () => ({
    width: 14,
    height: 7,
  }),
  triggerProps: () => ({
    asChild: false,
  }),
  contentProps: () => ({
    side: 'top',
    align: 'center',
    // default to 3px to match vue-tippy styles
    sideOffset: 3,
    alignOffset: 0,
  }),
  rootProps: () => ({
    delayDuration: 200,
  }),
  clickToToggle: true,
});
// 'click' wasn't emitting the same way vue-tippy was so I'm explicitly adding it here
const emits = defineEmits<TooltipRootEmits & { click: [value: any] }>();

// isHoverOpen: reka-driven (hover/focus). isClickOpen: click-toggle only.
// closedByClick: blocks reka from re-opening via hover after a click-close.
//   Set unconditionally — reka's delayDuration means @update:open(true) can fire
//   *after* handleClick, so a conditional set on isHoverOpen would miss it.
//   Known limitation: tooltip DOM unmount can re-fire @pointerenter, clearing this
//   flag and allowing hover to immediately re-open. Acceptable edge case.
// forceClosedByExternal: reka fires @pointer-down-outside in capture phase, BEFORE
//   the element click. Tells handleClick not to re-open after that outside-click.
const isHoverOpen = ref(false);
const isClickOpen = ref(false);
const closedByClick = ref(false);
const forceClosedByExternal = ref(false);
let forceClosedTimer: ReturnType<typeof setTimeout> | undefined;
// Guards handleFocusOut against the transient focusout Enter-activation fires
// synchronously after handleClick. Set in handleClick, cleared via nextTick.
let suppressFocusOut = false;

onUnmounted(() => clearTimeout(forceClosedTimer));

// Caller passes rootProps.open → controlled mode: pass rootProps through unchanged.
// Internal refs still update but their values are not used to override open.
const isControlled = computed(() => props.rootProps?.open !== undefined);
// rootProps.disabled (e.g. no tooltip content) must win over click-toggle state,
// otherwise a stale isClickOpen forces `open: true` and shows an empty tooltip.
const isDisabled = computed(() => props.rootProps?.disabled === true);

const tooltipRootProps = computed(() => {
  if (!props.clickToToggle || isControlled.value || isDisabled.value) return props.rootProps;
  const hoverWins = isHoverOpen.value && !closedByClick.value;
  return { ...props.rootProps, open: hoverWins || isClickOpen.value };
});

const handleUpdateOpen = (val: boolean) => {
  if (props.clickToToggle) {
    isHoverOpen.value = val;
    emits('update:open', (isHoverOpen.value && !closedByClick.value) || isClickOpen.value);
  }
};

const handlePointerEnter = () => {
  if (props.clickToToggle) {
    closedByClick.value = false;
    forceClosedByExternal.value = false;
  }
};

const handleClick = (event: MouseEvent) => {
  if (props.clickToToggle && !isDisabled.value) {
    suppressFocusOut = true;
    nextTick(() => {
      suppressFocusOut = false;
    });
    if (forceClosedByExternal.value) {
      // Outside-click already closed; set closedByClick to block reka's delayed hover re-open.
      forceClosedByExternal.value = false;
      closedByClick.value = true;
    } else {
      // Toggle: close if currently visible (hover or click), open if not.
      const isCurrentlyVisible = (isHoverOpen.value && !closedByClick.value) || isClickOpen.value;
      if (isCurrentlyVisible) {
        isClickOpen.value = false;
        closedByClick.value = true;
      } else {
        isClickOpen.value = true;
        closedByClick.value = false;
      }
      emits('update:open', (isHoverOpen.value && !closedByClick.value) || isClickOpen.value);
    }
  }
  emits('click', event);
};

// suppressFocusOut skips the transient focusout Enter fires synchronously with handleClick.
const handleFocusOut = () => {
  if (!props.clickToToggle) return;
  if (suppressFocusOut) return;
  isClickOpen.value = false;
  closedByClick.value = false;
};

// Escape: full reset without setting forceClosedByExternal.
const handleEscapeKeyDown = () => {
  if (props.clickToToggle) {
    isHoverOpen.value = false;
    isClickOpen.value = false;
    closedByClick.value = false;
  }
};

// Reka fires pointer-down-outside in capture phase, before the element click.
// setTimeout(0) clears the flag after the event chain — handles click-elsewhere with no trigger follow-up.
const handlePointerDownOutside = () => {
  if (props.clickToToggle) {
    isHoverOpen.value = false;
    isClickOpen.value = false;
    closedByClick.value = false;
    forceClosedByExternal.value = true;
    clearTimeout(forceClosedTimer);
    forceClosedTimer = setTimeout(() => {
      forceClosedByExternal.value = false;
    }, 0);
  }
};

// Lets a parent force an uncontrolled tooltip shut, eg. when its trigger is
// about to be hidden — reka only closes on Escape/outside-click/blur, not
// when the trigger vanishes with no mouseleave to notice it's gone.
const close = () => {
  isHoverOpen.value = false;
  isClickOpen.value = false;
  closedByClick.value = false;
};

defineExpose({ close });
</script>

<template>
  <TooltipRoot v-bind="tooltipRootProps" @update:open="handleUpdateOpen">
    <TooltipTrigger
      v-bind="props.triggerProps"
      @click="handleClick"
      @focusout="handleFocusOut"
      @pointerenter="handlePointerEnter"
      :class="
        cnMerge(
          'focus:outline focus:outline-2 focus:outline-blue-400 dark:focus:outline-blue-500',
          props.triggerProps?.class,
        )
      "
      :data-testid="props.dataTestid ? `${props.dataTestid}-trigger` : null"
      :aria-label="props.ariaLabel"
      :aria-expanded="
        props.clickToToggle && !isControlled
          ? (isHoverOpen && !closedByClick) || isClickOpen
          : undefined
      "
    >
      <slot name="trigger" />
    </TooltipTrigger>
    <TooltipPortal :to="portalTo">
      <TooltipContent
        v-bind="props.contentProps"
        :data-testid="props.dataTestid ? `${props.dataTestid}-container` : null"
        @escape-key-down="handleEscapeKeyDown"
        @pointer-down-outside="handlePointerDownOutside"
        :class="
          cnMerge(
            'katooltip-content relative z-[9999] whitespace-normal rounded border border-gray-200 bg-white shadow-sm outline-none data-[state=delayed-open]:data-[side=bottom]:animate-slideDownAndFade data-[state=delayed-open]:data-[side=left]:animate-slideRightAndFade data-[state=delayed-open]:data-[side=right]:animate-slideLeftAndFade data-[state=delayed-open]:data-[side=top]:animate-slideUpAndFade data-[state=instant-open]:data-[side=bottom]:animate-slideDownAndFade data-[state=instant-open]:data-[side=left]:animate-slideRightAndFade data-[state=instant-open]:data-[side=right]:animate-slideLeftAndFade data-[state=instant-open]:data-[side=top]:animate-slideUpAndFade dark:border-navy-600 dark:bg-navy-800',
            props.contentProps?.class,
          )
        "
      >
        <div
          :data-testid="props.dataTestid || null"
          :class="
            cnMerge(
              'tooltip-content max-w-96 p-2 px-4 text-left text-sm font-medium text-gray-900 dark:text-gray-200',
              props.contentContainerClass,
            )
          "
        >
          <template v-if="props.content">
            {{ props.content }}
          </template>
          <template v-else>
            <slot name="tooltip" />
          </template>
        </div>
        <TooltipArrow
          v-bind="props.arrowProps"
          :class="
            cnMerge(
              'fill-white stroke-gray-200 dark:fill-navy-800 dark:stroke-navy-600',
              props.arrowProps?.class,
            )
          "
        />
      </TooltipContent>
    </TooltipPortal>
  </TooltipRoot>
</template>
